import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLandingPageTitleColor } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./quizStage.css";
import "./crosswordStage.css";
import "./stageActions.css";
import {
  getChoiceSlotsForQuestion,
  getPointsForChoice,
  parseQuizFromTheme,
} from "./quizStageUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";
const STAGE_NUM = 4;

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage4() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage4Points, setStage4Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [answering, setAnswering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questionMotion, setQuestionMotion] = useState("");

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const stage4PointsRef = useRef(0);
  const questionIndexRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const questions = useMemo(() => parseQuizFromTheme(themeData), [themeData]);

  const title = useMemo(() => {
    const t =
      themeData?.quiz_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Quiz";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.quiz_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Choose the option that best reflects your perspective.";
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const currentQuestion = questions[questionIndex];

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("quiz-page");
    return () => {
      document.body.classList.remove("quiz-page");
    };
  }, []);

  useEffect(() => {
    questionIndexRef.current = questionIndex;
  }, [questionIndex]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage4Points);
  }, [stage4Points]);

  const finishStage = useCallback(async (skip = false, scoreOverride) => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const uid =
      storedUser.userId || storedUser.userid || storedUser.id;
    const sessionId = storedUser.sessionId;
    const organizationId = storedUser.organizationId;
    const timeStr = formatSecondsToClock(elapsedRef.current);
    const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
    const stage4Score = skip
      ? 0
      : scoreOverride ?? stage4PointsRef.current;
    const finalTotal = otherStagesTotalRef.current + stage4Score;

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: STAGE_NUM,
        score: stage4Score,
        time: timeStr,
        total_score: finalTotal,
        currentQues: skip ? questionIndexRef.current : questions.length,
      });
    }

    clearStageActive(4);
    markStageCompleted(4, storedUser);

    await showStageCompleteCelebration({
      stageNumber: 4,
      skip,
      score: stage4Score,
    });

    if (isDemoBypass) {
      window.location.href =
        String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      return;
    }
    navigate("/stage5");
  }, [
    submitting,
    storedUser,
    isDemoBypass,
    backendBase,
    adminToken,
    questions.length,
    navigate,
  ]);

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "quiz_rules"),
      themeData,
    });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded" || !questions.length) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st, {
            questionCount: questions.length,
          });
          const resumeRoute = getRedirectForPlayedStage(st, 4, {
            questionCount: questions.length,
          });
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage4 = Number(st.stage4) || 0;
          const resumeQ = Math.max(
            0,
            Math.min(
              questions.length,
              Number(st.currentQues) || 0
            )
          );

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage4);
          stage4PointsRef.current = dbStage4;
          setStage4Points(dbStage4);
          setDisplayTotalScore(dbTotal);

          setQuestionIndex(resumeQ);
          questionIndexRef.current = resumeQ;
        }

        setStageActive(4, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage4 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    questions.length,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  const handleChoice = useCallback(
    async (choiceKey) => {
      if (answering || submitting || !currentQuestion) return;
      setAnswering(true);
      setSelectedKey(choiceKey);

      const awarded = getPointsForChoice(currentQuestion, choiceKey);
      const nextStage4Points = stage4PointsRef.current + awarded;
      stage4PointsRef.current = nextStage4Points;
      setStage4Points(nextStage4Points);

      const nextIndex = questionIndex + 1;
      const isLast = nextIndex >= questions.length;

      if (isLast) {
        setTimeout(() => {
          setSelectedKey(null);
          setAnswering(false);
          finishStage(false, nextStage4Points);
        }, 500);
        return;
      }

      setTimeout(() => setQuestionMotion("exit"), 320);

      setTimeout(() => {
        setQuestionIndex(nextIndex);
        setSelectedKey(null);
        setQuestionMotion("enter");
      }, 700);

      setTimeout(() => {
        setQuestionMotion("");
        setAnswering(false);
      }, 1200);
    },
    [
      answering,
      submitting,
      currentQuestion,
      questionIndex,
      questions.length,
      finishStage,
    ]
  );

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="quiz-stage__loading">Loading game…</div>;
  }

  if (!questions.length) {
    return (
      <div className="quiz-stage__empty">
        No quiz questions configured. Add them in theme admin under Quiz.
      </div>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  const displayNum = questionIndex + 1;
  const choiceSlots = getChoiceSlotsForQuestion(currentQuestion);

  return (
    <div className="quiz-stage">
      <header className="quiz-stage__header">
        <div className="quiz-stage__title-block">
          <span className="quiz-stage__badge">STAGE 4</span>
          <h1 className="quiz-stage__title" style={themedTextStyle}>
            {title}
          </h1>
          <p className="quiz-stage__subtitle" style={themedTextStyle}>
            {subtitle}
          </p>
          <div
            className={`quiz-stage__question-progress${
              questionMotion === "enter"
                ? " quiz-stage__question-progress--swoosh"
                : ""
            }`}
            style={themedTextStyle}
          >
            Question <em>{displayNum}</em> / {questions.length}
          </div>
        </div>
        <div className="quiz-stage__hud">
          <div className="quiz-stage__hud-card">
            <div className="quiz-stage__hud-stat">
              <span aria-hidden>⏱</span>
              <span>{formatSecondsToClock(elapsedSec)}</span>
            </div>
            <div className="quiz-stage__hud-stat">
              <span aria-hidden>★</span>
              <span>{displayTotalScore}</span>
            </div>
          </div>
          <div className="stage-hud-actions">
            <button
              type="button"
              className="crossword-stage__help-btn"
              aria-label="How to play"
              onClick={showHelp}
            >
              ?
            </button>
            <button
              type="button"
              className="game-stage-actions__skip-btn game-stage-actions__skip-btn--hud"
              disabled={submitting || answering}
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      <div className="quiz-stage__panel">
        {questionMotion === "exit" ? (
          <div className="quiz-stage__swoosh-streak" aria-hidden />
        ) : null}
        <div
          className={`quiz-stage__question-block${
            questionMotion
              ? ` quiz-stage__question-block--${questionMotion}`
              : ""
          }`}
        >
          <h2 className="quiz-stage__question" style={themedTextStyle}>
            {currentQuestion.question}
          </h2>
          <div className="quiz-stage__options">
            {choiceSlots.map((choice) => (
              <button
                key={choice.key}
                type="button"
                className={`quiz-stage__option${
                  selectedKey === choice.key
                    ? " quiz-stage__option--selected"
                    : ""
                }`}
                style={{
                  background: choice.cardBg,
                  borderColor:
                    selectedKey === choice.key ? choice.accent : "transparent",
                }}
                disabled={answering || submitting}
                onClick={() => handleChoice(choice.key)}
              >
                <span
                  className="quiz-stage__option-label"
                  style={themedTextStyle}
                >
                  {choice.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
